import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { SelfUser } from '../shared/types/api';
import { useAuth } from '../hooks/AuthContext';
import { ErrorBox, Loading, PageHeader } from './Ui';

/**
 * Renders `children` for a signed-in person. Anonymous visitors are sent to sign in and come back here;
 * if the server cannot be reached the page says so, instead of mistaking "unknown" for "signed out".
 */
export function RequireUser({ children }: { children: (user: SelfUser) => ReactNode }) {
  const { user, unavailable, refresh } = useAuth();
  const location = useLocation();
  if (user === undefined) {
    return (
      <div className="container">
        <Loading />
      </div>
    );
  }
  if (user === null) {
    if (unavailable) {
      return (
        <div className="container medium">
          <PageHeader
            title="Your workspace is temporarily unavailable"
            description="Reconnect to Library to continue to this page."
          />
          <ErrorBox error={unavailable} retry={() => void refresh()} />
        </div>
      );
    }
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(location.pathname + location.search + location.hash)}`}
        replace
      />
    );
  }
  return <>{children(user)}</>;
}
